import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import HolographicPortrait from './components/HolographicPortrait';
import InteractiveTimeline from './components/InteractiveTimeline';
import SkillConstellation from './components/SkillConstellation';
import DigitalPhilosophy from './components/DigitalPhilosophy';
import CertificationGallery from './components/CertificationGallery';
import PersonalityMatrix from './components/PersonalityMatrix';
import TestimonialCards from './components/TestimonialCards';

const IdentityMatrixAbout = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Helmet>
        <title>Identity Matrix - About | CyberDev Nexus</title>
        <meta name="description" content="Discover the multi-dimensional story of a digital architect transforming from traditional coder to cyberpunk visionary. Explore the interactive timeline, skill constellation, and digital philosophy." />
        <meta name="keywords" content="about, developer story, cyberpunk developer, digital architect, tech skills, certifications, testimonials" />
      </Helmet>

      <Header />

      <main className="relative">
        {/* Hero Section with Holographic Portrait */}
        <section className="relative pt-24 pb-16 overflow-hidden">
          <div className="absolute inset-0 particle-bg"></div>
          
          <div className="relative z-10 container mx-auto px-6">
            <motion.div
              className="text-center mb-16"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <motion.h1 
                className="font-orbitron font-bold text-4xl md:text-6xl text-foreground mb-6"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                Identity <span className="text-brand-cyan">Matrix</span>
              </motion.h1>
              <motion.p 
                className="text-xl text-text-secondary font-inter max-w-3xl mx-auto leading-relaxed"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
              >
                The multi-dimensional story of evolution from traditional coder to digital visionary, 
                where technology transcends boundaries and creates tomorrow's digital experiences
              </motion.p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              <HolographicPortrait />
            </motion.div>
          </div>

          {/* Floating Particles */}
          <div className="absolute inset-0 pointer-events-none">
            {[...Array(20)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute w-1 h-1 bg-brand-cyan rounded-full opacity-60"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`
                }}
                animate={{
                  y: [0, -30, 0],
                  opacity: [0.6, 1, 0.6],
                  scale: [1, 1.5, 1]
                }}
                transition={{
                  duration: 3 + Math.random() * 2,
                  repeat: Infinity,
                  delay: Math.random() * 2,
                  ease: "easeInOut"
                }}
              />
            ))}
          </div>
        </section>

        {/* Interactive Timeline Section */}
        <section className="py-20 relative">
          <div className="container mx-auto px-6">
            <InteractiveTimeline />
          </div>
        </section>

        {/* Skill Constellation Section */}
        <section className="py-20 relative bg-gradient-to-b from-transparent via-surface/10 to-transparent">
          <div className="container mx-auto px-6">
            <SkillConstellation />
          </div>
        </section>

        {/* Digital Philosophy Section */}
        <section className="py-20 relative">
          <div className="container mx-auto px-6">
            <DigitalPhilosophy />
          </div>
        </section>

        {/* Certification Gallery Section */}
        <section className="py-20 relative bg-gradient-to-b from-transparent via-surface/10 to-transparent">
          <div className="container mx-auto px-6">
            <CertificationGallery />
          </div>
        </section>

        {/* Personality Matrix Section */}
        <section className="py-20 relative">
          <div className="container mx-auto px-6">
            <PersonalityMatrix />
          </div>
        </section>

        {/* Testimonial Cards Section */}
        <section className="py-20 relative bg-gradient-to-b from-transparent via-surface/10 to-transparent">
          <div className="container mx-auto px-6">
            <TestimonialCards />
          </div>
        </section>

        {/* Call to Action Section */}
        <section className="py-20 relative">
          <div className="container mx-auto px-6">
            <motion.div
              className="text-center max-w-4xl mx-auto"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <div className="glass-morphism rounded-2xl p-12 cyber-border-active cyber-glow">
                <h2 className="font-orbitron font-bold text-3xl text-foreground mb-6">
                  Ready to Build the Future Together?
                </h2>
                <p className="text-text-secondary font-inter text-lg leading-relaxed mb-8">
                  The journey through the Identity Matrix reveals not just technical expertise, 
                  but a philosophy of technological transcendence. Let's collaborate to create digital experiences that push the boundaries of what's possible.
                </p>
                
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <motion.a
                    href="/neural-network-contact-collaboration"
                    className="inline-flex items-center justify-center px-8 py-4 bg-brand-cyan text-background font-inter font-semibold rounded-lg cyber-glow hover:cyber-glow-intense transition-all duration-300"
                    whileHover={{ scale: 1.05, y: -2 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <span>Initiate Collaboration</span>
                  </motion.a>
                  
                  <motion.a
                    href="/project-dimensions-portfolio"
                    className="inline-flex items-center justify-center px-8 py-4 cyber-border text-foreground font-inter font-semibold rounded-lg hover:cyber-border-active hover:cyber-glow transition-all duration-300"
                    whileHover={{ scale: 1.05, y: -2 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <span>Explore Project Dimensions</span>
                  </motion.a>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Background Elements */}
        <div className="fixed inset-0 pointer-events-none z-0">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-brand-cyan/5 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-brand-purple/5 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-brand-green/5 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '4s' }}></div>
        </div>
      </main>
    </div>
  );
};

export default IdentityMatrixAbout;